import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import {
  JourneyUi,
  convertToJourneySearchCriteria,
  FlightRuleDeleteRequest,
  INCLUDE_ACTION,
  JourneySearchCriteriaUi,
  FlightRuleSearchRequest,
  FlightRule,
  FLIGHT_RULE_DELETE_REQUEST_VERSION
} from '../model';
import { Store, select } from '@ngrx/store';
import { Router } from '@angular/router';
import { NavigationService } from '../../service/core/navigation.service';
import {
  SearchFlightRuleAction,
  ActivateFlightRuleDisplayAction,
  DeleteFlightRuleDisplayNotificationAction,
  ClearFlightRulesAction,
  DeleteFlightRuleSearchNotificationAction,
  DeleteFlightRuleAction,
  StartFlightModificationAction,
  CopyFlightRuleAction,
  DeleteFlightRuleCreateNotificationAction
} from '../../store/flight/flights-action';
import { BehaviorSubject, map, Observable, Subscription, combineLatest } from 'rxjs';
import { UppNotification } from '../../model/notification';
import {
  selectFlightRuleSearchSending,
  selectFlightRules,
  selectFlightRuleSearchNotification,
  selectFlightRuleDeleteSending,
  selectFlightCurrentPage,
  selectFlightPageSize,
  selectFlightTotalCount
} from '../../store/flight/flights-selector';
import {
  NotificationMessages,
  UppNotificationComponent
} from '../../components/upp-notification/upp-notification.component';
import { UserDetailsService } from '../../service/user-details.service';
import { UppComponentNames } from '../../service/model';
import {
  BasicSearchResultTableComponent,
  DisplayableField,
  EntryType
} from '../../base/search/basic-search-result-table/basic-search-result-table.component';
import {
  PERMISSION_MANAGE_EDIFACT_RULE,
  PERMISSION_MANAGE_FLIGHT,
  PERMISSION_MANAGE_NDC_RULE
} from '../../service/user-permissions.service';
import { FlightSearchCriteriaComponent } from './flight-search-criteria/flight-search-criteria.component';
import { ContentType } from '../model/journey';
import { getPropertyByKey } from '../../util/utils';
import { MarketPair } from '../../model/market-pair';
import { AsyncPipe } from '@angular/common';
import { LoadingAlertComponent } from '../../components/loading-alert/loading-alert.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HistoryModalComponent } from '../../components/history-modal/history-modal.component';
import { FlightService } from '../../service/flight.service';
import { HistoryRuleRecord } from '../../model/history-rule-record';
import { ConfigurationService } from '../../service/configuration/configuration.service';
import { FeatureFlags } from '../../core/util/resources';
import { FlightsState } from '../../store/flight/flights-state';
import { AgGridSearchTableComponent } from '../../base/search/ag-grid-search-table/ag-grid-search-table.component';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import { TableActionsComponent } from '../../components/table-actions/table-actions.component';
import { CopyToClipboardButtonComponent } from '../../components/copy-to-clipboard-button/copy-to-clipboard-button.component';

@Component({
  selector: 'ama-ng-upp-flights-search',
  templateUrl: './flights-search.component.html',
  standalone: true,
  imports: [
    LoadingAlertComponent,
    UppNotificationComponent,
    FlightSearchCriteriaComponent,
    BasicSearchResultTableComponent,
    AsyncPipe,
    AgGridAngular
  ]
})
export class FlightsSearchComponent extends AgGridSearchTableComponent implements OnInit, OnDestroy {
  @ViewChild(FlightSearchCriteriaComponent)
  searchCriteriaComponent!: FlightSearchCriteriaComponent;

  flightRuleSearchSending$: Observable<boolean>;
  flightRuleDeleteSending$: Observable<boolean | undefined>;
  flightRules$: Observable<JourneyUi[]>;
  transformedFlightRules$: Observable<FlightRule[]>;
  notification$: Observable<UppNotification | undefined>;
  currentPage$: Observable<number>;
  pageSize$: Observable<number>;
  totalCount$: Observable<number>;

  currentPage = 0;
  pageSize = 10;
  private lastSearchCriteria: JourneySearchCriteriaUi | null = null;

  mainMessages: NotificationMessages;

  subscription: Subscription = new Subscription();

  displayRuleHistory = false;
  managePermission = PERMISSION_MANAGE_FLIGHT;

  troubleShootingEnabled = false;
  displayableFields!: DisplayableField[];

  agGridTablesActivated = false;
  paginationEnabled = false;
  activeConfirmationRowId$ = new BehaviorSubject<string | null>(null);

  private readonly displayableFieldsWithId = [
    {
      value: 'id',
      label: 'Rule ID',
      isSortable: true,
      hasCopyToClipboard: true,
      displayShortened: true
    },
    {
      value: 'name',
      label: 'Name',
      isSortable: true
    },
    {
      value: 'pointOfSaleNames',
      label: 'Point of sale',
      isSortable: true
    },
    {
      value: 'firstMarketNames',
      label: 'First market'
    },
    {
      value: 'secondMarketNames',
      label: 'Second market'
    },
    {
      value: 'ruleStatusActive',
      label: 'Rule status',
      isSortable: true
    },
    {
      value: 'description',
      label: 'Description'
    }
  ];

  private readonly displayableFieldsWithoutId = [
    {
      value: 'name',
      label: 'Name',
      isSortable: true
    },
    {
      value: 'pointOfSaleNames',
      label: 'Point of sale',
      isSortable: true
    },
    {
      value: 'firstMarketNames',
      label: 'First market'
    },
    {
      value: 'secondMarketNames',
      label: 'Second market'
    },
    {
      value: 'ruleStatusActive',
      label: 'Rule status',
      isSortable: true
    },
    {
      value: 'description',
      label: 'Description'
    }
  ];

  constructor(
    store: Store<FlightsState>,
    router: Router,
    navigationService: NavigationService,
    private readonly dfModalService: NgbModal,
    private readonly userDetailsService: UserDetailsService,
    private readonly flightService: FlightService,
    private readonly configurationService: ConfigurationService
  ) {
    super(store, router, navigationService);

    this.flightRuleSearchSending$ = this.store.pipe(select(selectFlightRuleSearchSending));
    this.flightRuleDeleteSending$ = this.store.pipe(select(selectFlightRuleDeleteSending));
    this.flightRules$ = this.store.pipe(select(selectFlightRules));
    this.currentPage$ = this.store.pipe(select(selectFlightCurrentPage));
    this.pageSize$ = this.store.pipe(select(selectFlightPageSize));
    this.totalCount$ = this.store.pipe(select(selectFlightTotalCount));

    this.transformedFlightRules$ = combineLatest([
      this.flightRules$,
      this.currentPage$,
      this.pageSize$,
      this.totalCount$,
      this.flightRuleSearchSending$
    ]).pipe(
      map(([flightRules, currentPage, pageSize, totalCount, isLoading]) => {
        const transformedRules = (flightRules || [])
          .filter((fr) => fr && !fr.statusType)
          .map((fr) => this.recordToSearchResult(fr));

        if (this.paginationEnabled && totalCount > 0) {
          // Sparse array with length=totalCount so ag-grid calculates correct page count.
          // Each placeholder gets a unique __idx so getRowId never returns undefined.
          const fullArray = Array.from({ length: totalCount }, (_, i) => ({ __idx: i })) as any[];

          if (!isLoading && transformedRules.length > 0) {
            // Only populate with actual data when not loading
            const startIndex = currentPage * pageSize;
            transformedRules.forEach((rule, index) => {
              fullArray[startIndex + index] = rule;
            });
          }

          return fullArray;
        }

        return transformedRules;
      })
    );

    this.notification$ = this.store.pipe(select(selectFlightRuleSearchNotification));
    this.mainMessages = {
      error: $localize`:@@upp.global.text.mainErrorText:The request failed due to the following errors:`,
      warning: $localize`:@@upp.global.text.mainWarningText:Warnings were generated during the search process:`,
      success: $localize`:@@upp.global.text.mainSuccessText:The search was successful.`
    };

    this.configurationService.getParameter$(FeatureFlags.troubleshootingPanel).subscribe((isEnabled) => {
      this.troubleShootingEnabled = isEnabled ?? false;
    });

    this.configurationService.getParameter$(FeatureFlags.agGridTables).subscribe((isEnabled) => {
      this.agGridTablesActivated = isEnabled ?? false;
    });

    this.configurationService.getParameter$(FeatureFlags.pagination).subscribe((isEnabled) => {
      this.paginationEnabled = isEnabled ?? false;
    });
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.displayableFields = this.troubleShootingEnabled
      ? this.displayableFieldsWithId
      : this.displayableFieldsWithoutId;

    this.subscription.add(
      this.configurationService.getParameter$(FeatureFlags.ruleHistory)?.subscribe((ruleHistory) => {
        this.displayRuleHistory = ruleHistory ?? false;
      })
    );
    this.navigationService.setSelectedMenuTitle($localize`:@@upp.flights.search.navigationTitle:Flight Rule Search`);
    this.store.dispatch(new ClearFlightRulesAction({}));
    this.navigationService.activateFlights();
    this.navigationService.enableNavigation();

    this.displayableFields.push({
      value: 'airContentType',
      label: 'Air content type',
      isSortable: true
    });

    if (this.agGridTablesActivated) {
      this.initializeColumnDefs();
    }

    this.subscription.add(
      this.currentPage$.subscribe((page) => {
        this.currentPage = page;
      })
    );

    this.subscription.add(
      this.pageSize$.subscribe((size) => {
        this.pageSize = size;
      })
    );

    this.store.dispatch(new DeleteFlightRuleCreateNotificationAction({}));
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  searchFlightRules(searchCriteria: JourneySearchCriteriaUi) {
    this.lastSearchCriteria = searchCriteria;
    this.currentPage = 0;

    const request = this.createFlightRulesSearchRequest(searchCriteria);
    this.store.dispatch(new SearchFlightRuleAction({ request }));

    if (this.gridApi) {
      this.gridApi.paginationGoToPage(0);
    }
  }

  createFlightRulesSearchRequest(searchCriteria: JourneySearchCriteriaUi): FlightRuleSearchRequest {
    const request: FlightRuleSearchRequest = {
      version: '2.0',
      searchJourney: convertToJourneySearchCriteria(searchCriteria)
    };
    if (this.paginationEnabled) {
      request.searchFollowUpRequest = {
        requestedPageNumber: String(this.currentPage + 1),
        numberOfRecords: String(this.pageSize)
      };
    }
    request.searchJourney.rule = this.userDetailsService.assignEntity(request.searchJourney?.rule);
    return request;
  }

  closeNotification() {
    this.store.dispatch(new DeleteFlightRuleSearchNotificationAction({}));
  }

  clearFlightRules() {
    this.store.dispatch(new ClearFlightRulesAction({}));
    this.router.navigate(['flights/search']);
  }

  showFlightRule(id: string) {
    this.store.dispatch(new ActivateFlightRuleDisplayAction({ id: undefined }));
    this.store.dispatch(new DeleteFlightRuleDisplayNotificationAction({}));
    this.router.navigate(['flights/display/' + id]); // NOSONAR
  }

  modifyFlightRule(flightRuleId: string) {
    this.store.dispatch(new StartFlightModificationAction({ id: flightRuleId }));
    this.router.navigate(['flights/modify/']);
  }

  storeUnsavedFlightSearch() {
    this.searchCriteriaComponent.storeUnsavedFlightSearch();
  }

  copyFlightRule(flightRuleId: string) {
    this.store.dispatch(new CopyFlightRuleAction({ id: flightRuleId }));
    this.store.dispatch(new DeleteFlightRuleCreateNotificationAction({}));
    this.router.navigate(['flights/create/']);
  }

  deleteFlightRule(request: FlightRuleDeleteRequest) {
    this.store.dispatch(new DeleteFlightRuleAction({ request }));
  }

  getSourceComponent(): UppComponentNames {
    return UppComponentNames.FLIGHTS;
  }

  getEntryType(): EntryType {
    return EntryType.JOURNEY;
  }

  showHistory(event: { id: string; name: string }) {
    const modalRef = this.dfModalService.open(HistoryModalComponent, {
      size: 'xl',
      scrollable: true,
      centered: true,
      backdrop: 'static'
    });

    modalRef.componentInstance.ruleName.set(event.name);
    modalRef.componentInstance.isLoading.set(true);
    modalRef.componentInstance.rowData = [];

    this.subscription.add(
      this.flightService.getRuleHistory(event.id).subscribe({
        next: (data: HistoryRuleRecord[]) => {
          modalRef.componentInstance.rowData = data;
          modalRef.componentInstance.isLoading.set(false);
        },
        error: () => {
          modalRef.componentInstance.rowData = [];
          modalRef.componentInstance.isLoading.set(false);
        }
      })
    );
  }

  getFlightSearchRecordPermissions(record: any): string[] {
    const neededPermissions = [];

    // Old records do not have 'contentTypes' defined, so
    // they are treated as EDIFACT records.
    if (!record.applicability.contentTypes || record.applicability.contentTypes?.includes(ContentType.EDIFACT)) {
      neededPermissions.push(PERMISSION_MANAGE_EDIFACT_RULE);
    }

    if (record.applicability.contentTypes?.includes(ContentType.NDC)) {
      neededPermissions.push(PERMISSION_MANAGE_NDC_RULE);
    }

    return neededPermissions;
  }

  recordToSearchResult(flightRecord: any): any {
    const pointOfSaleNames = this.getPosOrMarketNames(flightRecord.applicability.pointOfSaleNames);
    const searchResult = {
      id: flightRecord.rule.id,
      name: flightRecord.rule.name,
      description: flightRecord.rule.description ? flightRecord.rule.description : '',
      organization: flightRecord.rule.organization,
      version: flightRecord.rule.version,
      pointOfSaleNames,
      originNames: this.getPosOrMarketNames(flightRecord.applicability.originNames),
      destinationNames: this.getPosOrMarketNames(flightRecord.applicability.destinationNames),
      actionType: calculateActionType(flightRecord),
      ruleStatusActive: flightRecord.rule.active,
      firstMarketNames: getMarketNames(flightRecord.applicability.marketPairs, 'firstMarketName'),
      secondMarketNames: getMarketNames(flightRecord.applicability.marketPairs, 'secondMarketName')
    };

    searchResult['airContentType' as keyof typeof searchResult] =
      flightRecord.applicability.contentTypes?.join(',') ?? ContentType.EDIFACT;

    return searchResult;
  }

  getPosOrMarketNames(names: string[] | string): string {
    if (names === undefined || names.length === 0) {
      return '';
    }
    return names.toString();
  }

  entryToUpdateCreator(
    id: string,
    name: string,
    organization: string,
    version: string,
    ruleVersion: number
  ): FlightRuleDeleteRequest {
    return {
      version,
      journey: {
        rule: {
          id,
          name,
          version: ruleVersion
        }
      }
    };
  }

  entryToUpdateNameResolver(entryToUpdate: FlightRuleDeleteRequest) {
    return entryToUpdate.journey.rule.name;
  }

  getDisplayHandler(): (data: unknown) => void {
    return (data: unknown) => this.showFlightRule((data as FlightRule).id ?? '');
  }

  getModifyHandler(): (data: unknown) => void {
    return (data: unknown) => this.modifyFlightRule((data as FlightRule).id ?? '');
  }

  getCopyHandler(): (data: unknown) => void {
    return (data: unknown) => this.copyFlightRule((data as FlightRule).id ?? '');
  }

  getDeleteHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const flightRule = data as FlightRule;
      const deleteRequest = {
        version: FLIGHT_RULE_DELETE_REQUEST_VERSION,
        journey: {
          rule: {
            id: flightRule.id ?? '',
            version: flightRule.version ?? '1'
          }
        }
      } as FlightRuleDeleteRequest;

      this.deleteFlightRule(deleteRequest);
    };
  }

  getShowHistoryHandler(): (data: unknown) => void {
    return (data: unknown) => {
      const flightRule = data as FlightRule;
      this.showHistory({ id: flightRule.id ?? '', name: flightRule.name ?? '' });
    };
  }

  getDeleteSendingObservable(): Observable<boolean> {
    return this.flightRuleDeleteSending$.pipe(map((value) => value ?? false));
  }

  getNameField(): string {
    return 'name';
  }

  setActiveConfirmationRowId(rowId: string | null) {
    this.activeConfirmationRowId$.next(rowId);
  }

  override onGridReady(params: GridReadyEvent): void {
    super.onGridReady(params);

    this.subscription.add(
      combineLatest([this.currentPage$, this.pageSize$]).subscribe(([page, size]) => {
        if (!this.gridApi) {
          return;
        }
        if (size !== this.gridApi.paginationGetPageSize()) {
          this.gridApi.setGridOption('paginationPageSize', size);
        }
        this.gridApi.paginationGoToPage(page);
      })
    );
  }

  private initializeColumnDefs(): void {
    const dataColumns = this.displayableFields
      .filter((field) => field.value !== 'description')
      .map((field) => {
        const colDef: ColDef = {
          field: field.value,
          headerName: field.label,
          sortable: field.isSortable ?? false,
          unSortIcon: field.isSortable ?? false,
          resizable: false,
          suppressMovable: true,
          autoHeight: true
        };

        if (field.value === 'ruleStatusActive') {
          colDef.valueGetter = (params) => (params.data?.ruleStatusActive ? 'Active' : 'Inactive');
        }

        if (field.value === 'id') {
          colDef.cellRenderer = CopyToClipboardButtonComponent;
          colDef.minWidth = 200;
        }

        return colDef;
      });

    this.columnDefs = [
      ...dataColumns,
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        resizable: false,
        suppressMovable: true,
        autoHeight: true,
        flex: 2,
        minWidth: 230,
        wrapText: true,
        cellRenderer: TableActionsComponent,
        cellRendererParams: {
          showHistoryButton: this.displayRuleHistory
        }
      }
    ];

    this.gridOptions = {
      ...this.gridOptions,
      getRowId: (params) => params.data.id ?? `__ph_${params.data.__idx}`,
      getRowClass: (params) => (params.data?.id ? '' : 'ag-row-hidden'),
      postSortRows: (params) => {
        this.handlePostSortRowsForPagination(params, this.paginationEnabled);
      },
      onPaginationChanged: () => {
        if (!this.paginationEnabled || !this.gridApi || !this.lastSearchCriteria) {
          return;
        }
        const newPage = this.gridApi.paginationGetCurrentPage();
        const newPageSize = this.gridApi.paginationGetPageSize();

        if (newPage !== this.currentPage || newPageSize !== this.pageSize) {
          this.currentPage = newPage;
          this.pageSize = newPageSize;

          const request = this.createFlightRulesSearchRequest(this.lastSearchCriteria);
          this.store.dispatch(new SearchFlightRuleAction({ request }));
        }
      }
    };
  }
}

const calculateActionType = (journey: JourneyUi): string => {
  if (!journey.rule || journey.rule.action === undefined) {
    return '';
  }
  if (journey.rule.action === INCLUDE_ACTION) {
    return $localize`:@@upp.flights.search.table.include:Incl.`;
  }
  return $localize`:@@upp.flights.search.table.exclude:Excl.`;
};

const getMarketNames = (marketPairs: MarketPair[], property: string): string =>
  marketPairs
    ?.reduce((acc, marketPair) => {
      const marketProperty = getPropertyByKey(marketPair, property);
      if (marketProperty) {
        acc.push(marketProperty);
      }
      return acc;
    }, [] as string[])
    .join(', ') ?? '';
